/* eslint-disable indent-legacy */
import { delay } from 'redux-saga';
import { all, call, put, take, select, takeEvery, fork, race } from 'redux-saga/effects';

import { sanitizeErrorMessage } from '@xanadu/ui-shared/utils/fetchService';
import { GAME_NOT_FOUND_ERROR, LOBBY_TYPES } from '../../constants/casino';

import { displayModal } from '@xanadu/ui-shared/modules/modal/actions';

import { CHANGE_PRODUCT, INITIALISE, PAGE_VISIBILITY_CHANGE } from '@xanadu/ui-shared/modules/application/actions';

import {
  SET_USER
} from '@xanadu/ui-shared/modules/userSession/actions';

import {
  GET_LOBBY_VIEWS,
  getLobbyViews,
  GET_LOBBIES,
  setLobbiesByProduct,
  setLobbies,
  GET_GAME_REAL_MODE,
  setGameUrl,
  setAvailability,
  setGameUrlError,
  setCasinoBanners,
  setCasinoVisibilityRestricted,
  requestLobbies,
  getLobbies,
} from './actions';

import { setVirtualsVisibilityRestricted } from '@xanadu/ui-shared/modules/user/actions';

import { PRODUCTS } from '../../constants';

import * as api from './api';

const LOBBIES_POLLING = 1000 * 60 * 3;

const REQUEST_THRESHOLD = 1000 * 60 * 30; // 30 minutes

const LOBBIES_REQUEST_TIMES = Object.values(LOBBY_TYPES).reduce((accumulator, key) => ({
  ...accumulator,
  [key]: null,
}), {});

function* handleCasinoLobbiesRequests() {
  const product = yield select(state => state.getIn(['application', 'product']));
  // this is not very clean but it's going to work correctly until all the lobbies are all in the same api response
  const existingStoreLobby = yield select(state => state.getIn(['vegas', 'lobbies', LOBBY_TYPES[product], 'id']));
  if (!existingStoreLobby) {
    yield put(getLobbyViews());
  }
}

function* requestLobbyViews() {
  try {
    const response = yield call(api.fetchLobbyViews);

    const { lobbyViews = [], errors } = response;

    yield put(setAvailability(!errors));

    const dataClassic = lobbyViews.find(lobby => lobby.id === 'classic-casino');

    const dataLive = lobbyViews.find(lobby => lobby.id === 'live-casino');

    const dataVirtuals = lobbyViews.find(lobby => lobby.id === 'virtual-sports');

    yield put(setLobbies({
      lobbyViews: {
        ...(dataClassic && { 'classic-casino': dataClassic, }),
        ...(dataLive && { 'live-casino': dataLive }),
        ...(dataVirtuals && { virtuals: dataVirtuals }),
      }
    }));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching Vegas Content', error);
  }
}

function* requestLobbyData(payload) {
  const { payload: product, options = {} } = payload;

  const { polling = false } = options;

  try {
    const existingStoreLobby = yield select(state => state.getIn(['vegas', 'lobbies', LOBBY_TYPES[product]]));
    const casinoAvailable = yield select(state => state.getIn(['vegas', 'available']));
    const now = Date.now();

    if (
      !existingStoreLobby
      || polling
      || !casinoAvailable
      || (
        existingStoreLobby &&
        now > LOBBIES_REQUEST_TIMES[LOBBY_TYPES[product]] + REQUEST_THRESHOLD
      )
      || (
        existingStoreLobby && existingStoreLobby.error !== ''
      )
    ) {
      yield put(requestLobbies(product));
      const response = yield call(api.fetchLobbies, product);

      const { lobbies = [], errors } = response;
      let myLobbies = [];

      if (!errors) {
        LOBBIES_REQUEST_TIMES[LOBBY_TYPES[product]] = Date.now();
        let sections = [];

        const handleSectionUpdate = function* sectionUpdate(section, id) {
          if (section.id === 'a-z') {
            let myGames = {};
            let orderedLetters = [];
            if (section.lettersWithGames[0] === '#') {
              orderedLetters = [
                ...section.lettersWithGames.slice(1),
                '#'
              ];
            } else {
              orderedLetters = section.lettersWithGames;
            }
            myGames = yield call(api.fetchAZGames, {
              sectionId: section.id,
              lobbyId: id,
              product,
              letters: orderedLetters,
            });
            const mySection = {
              ...section,
              lettersWithGames: orderedLetters,
              // that's because the api returns the '#' as first key in the games list even if requested as last
              games: orderedLetters.reduce((acc, letter) => ({
                ...acc,
                [letter]: myGames[letter],
              }), {}),
            };
            return mySection;
          }
          return section;
        };

        const handleLobbyUpdate = function* lobbyUpdate(lobby) {
          sections = lobby.sections;
          if (sections.length > 0) {
            sections = yield sections.map((section) => handleSectionUpdate(section, lobby.id));
          }
          return {
            ...lobby,
            sections,
          };
        };

        myLobbies = yield lobbies.map(handleLobbyUpdate);

        yield put(setAvailability(!errors));

        yield put(setLobbiesByProduct({ lobbies: myLobbies, product: LOBBY_TYPES[product] }));
      } else {
        yield put(setAvailability(false));
      }
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error fetching Vegas Content', e);
  }
}

// here create a dedicated function to poll data again with current product,lobby
function* handlePollLobbies() {
  const casinoProducts = [
    PRODUCTS.casino,
    PRODUCTS.liveCasino,
    PRODUCTS.virtuals
  ];

  while (true) {
    const { payload: product } = yield take(CHANGE_PRODUCT);


    let isCasinoProduct = casinoProducts.includes(product);

    let currentProduct = product;

    while (isCasinoProduct) {
      const { cancel, task } = yield race({
        task: call(delay, LOBBIES_POLLING),
        cancel: take(CHANGE_PRODUCT),
        visibility: take(PAGE_VISIBILITY_CHANGE),
      });

      if (task) {
        yield put(getLobbies(currentProduct, { polling: true }));
      }

      const { payload: productUpdate } = cancel || {};

      if (productUpdate) {
        currentProduct = productUpdate;
      }

      if (productUpdate && !casinoProducts.includes(productUpdate)) {
        isCasinoProduct = false;
      }
    }
  }
}

function* handleRealMode({ payload }) {
  try {
    const product = yield select(state => state.getIn(['application', 'product'], []));
    const response = yield call(api.getRealModeGame, payload);
    const { statusCode, errors, launchUrl } = response;

    if (!errors) {
      yield put(setGameUrl({ gameRealModeUrl: launchUrl }));
    }

    if (errors) {
      if (response.statusCode === 422) {
        yield put(displayModal({
          show: true,
          section: 'casino-reality-check',
          showCloseIcon: false,
          data: {
            gameId: payload,
          },
        }));
      }

      if (statusCode === 403 && product !== PRODUCTS.virtuals) {
        yield put(setCasinoVisibilityRestricted(true));
      }

      if (statusCode === 403 && product === PRODUCTS.virtuals) {
        yield put(setVirtualsVisibilityRestricted(false));
      }

      if (statusCode === 404) {
        yield put(setGameUrlError(GAME_NOT_FOUND_ERROR));
      } else {
        yield put(setGameUrlError(sanitizeErrorMessage(errors)));
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error getting Vegas Game', error);
  }
}

function* handleBannerRequest() {
  try {
    const casinoBannerResponse = yield call(api.getCasinoBanner);
    const { statusCode, contentlets } = casinoBannerResponse;

    if (statusCode === 200) {
      yield put(setCasinoBanners(contentlets));
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error fetching Vegas Banners', e);
  }
}

function* watchSetUser() {
  while (true) {
    const { payload: user } = yield take(SET_USER);
    const { casinoStatus } = user;
    yield put(setCasinoVisibilityRestricted(casinoStatus === 'suspended'));
  }
}

export default function* root() {
  yield all([
    takeEvery(INITIALISE, handleCasinoLobbiesRequests),
    takeEvery(GET_LOBBY_VIEWS, requestLobbyViews),
    takeEvery(GET_LOBBIES, requestLobbyData),
    takeEvery(GET_GAME_REAL_MODE, handleRealMode),
    fork(handleBannerRequest),
    fork(watchSetUser),
    fork(handlePollLobbies),
  ]);
}
